/**
 * SAFE-1 — device physical command confirm gate.
 * Soft≠Live: operators confirm target + effect before siren/reboot/alert mutate.
 * No backend paths painted in the drawer. SPRING 620 preserved.
 * Schedule reboot: danger tone · delay/SSID facts · effect list · ack before send.
 */
import _React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, SPRING, SOFT } from "./ui.js";

const PHYSICAL = new Set([
  "alert_on",
  "alert_off",
  "silence",
  "reboot",
  "schedule_reboot",
  "self_test",
  "hold",
  "release",
]);

const REBOOT_KINDS = new Set(["reboot", "schedule_reboot"]);

export function isPhysicalDeviceCommand(kind) {
  return PHYSICAL.has(String(kind || "").trim().toLowerCase());
}

function labelOf(kind) {
  const k = String(kind || "").toLowerCase();
  if (k === "alert_on" || k === "sound") return "Sound siren";
  if (k === "alert_off" || k === "silence") return "Silence siren";
  if (k === "reboot") return "Reboot device";
  if (k === "schedule_reboot") return "Schedule reboot";
  if (k === "wifi_config" || k === "wifi_switch") return "Switch Wi-Fi";
  if (k === "self_test") return "Run self-test";
  if (k === "hold") return "Hold closed";
  if (k === "release") return "Release hold";
  if (k === "bulk_silence") return "Silence sirens in this window";
  if (k === "ota_accept" || k === "accept") return "Accept board campaign";
  if (k === "ota_decline" || k === "decline") return "Decline board campaign";
  if (k === "ota_rollback" || k === "rollback") return "Roll back board campaign";
  if (k === "ota_cancel" || k === "cancel") return "Cancel board campaign";
  if (k === "ota_publish" || k === "publish") return "Publish board build";
  if (k === "cap_execute_wave" || k === "execute_wave") return "Execute capability wave";
  return "Send device command";
}

function ConfirmDrawer({
  kind,
  title,
  targets,
  detail,
  confirmLabel,
  facts = [],
  effects = [],
  ackLabel = "",
  onYes,
  onNo,
}) {
  const reboot = REBOOT_KINDS.has(String(kind || "").toLowerCase());
  const needAck = reboot || !!ackLabel;
  const [acked, setAcked] = useState(!needAck);
  const factRows = Array.isArray(facts) ? facts.filter((f) => f && (f.k || f.label)) : [];
  const effectRows = Array.isArray(effects) ? effects.filter(Boolean) : [];

  return html`
    <${AnimatePresence}>
      <${M.div}
        key="dcg"
        className="scrim cmc-drawer-scrim"
        role="presentation"
        data-kind=${kind || ""}
        data-tone=${reboot ? "danger" : "safe"}
        initial=${{ opacity: 0 }}
        animate=${{ opacity: 1 }}
        exit=${{ opacity: 0 }}
        transition=${SOFT}
        onClick=${onNo}>
        <${M.div}
          className="sheetlet cmc-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dcg-title"
          data-cmc-safe="device-command-gate"
          data-kind=${kind || ""}
          data-tone=${reboot ? "danger" : "safe"}
          initial=${{ opacity: 0, y: 18 }}
          animate=${{ opacity: 1, y: 0 }}
          transition=${SPRING}
          onClick=${(e) => e.stopPropagation()}>
          <div className="cmc-drawer__in">
            <header className="cmc-drawer__hd">
              <div className="cmc-drawer__hd-copy">
                <span className="cmc-drawer__k">${reboot ? "Confirm · restart gate" : "Confirm before send"}</span>
                <h3 className="cmc-drawer__title" id="dcg-title">${title}</h3>
              </div>
              <button type="button" className="cmc-drawer__x" onClick=${onNo} aria-label="Cancel">
                <${Ico} d="close" w=${14}/>
              </button>
            </header>
            <p className="cmc-drawer__lede">
              ${detail || "This reaches real hardware. Review the target list, then confirm."}
            </p>

            ${factRows.length ? html`
              <section className="cmc-drawer__sec cmc-drawer__facts" aria-label="Command facts">
                <header className="cmc-drawer__sec-hd">
                  <span>Check these</span>
                  <b>Read-only</b>
                </header>
                <div className="cmc-drawer__factgrid">
                  ${factRows.map((f) => html`
                    <div key=${String(f.k || f.label)} className="cmc-drawer__fact"
                      data-ro=${f.readOnly === false ? "0" : "1"}
                      data-tone=${f.tone || "idle"}>
                      <span>${f.k || f.label}</span>
                      <b>${f.v != null && String(f.v).trim() ? f.v : "—"}</b>
                    </div>`)}
                </div>
              </section>` : null}

            <section className="cmc-drawer__sec">
              <header className="cmc-drawer__sec-hd">
                <span>${reboot ? "This unit" : "Targets"}</span>
                <b>${targets.length}</b>
              </header>
              <div className="cmc-drawer__list">
                ${targets.slice(0, 12).map((t) => html`
                  <div key=${t.id || t} className="cmc-drawer__row cmc-drawer__row--unit"
                    data-reboot=${reboot ? "1" : "0"}>
                    <div className="cmc-drawer__unit-main">
                      <b>${t.label || t.id || t}</b>
                      ${t.id && t.label ? html`<code>${t.id}</code>` : null}
                    </div>
                    ${t.ssid != null || t.presence || t.link ? html`
                      <dl className="cmc-drawer__unit-meta">
                        ${t.ssid != null ? html`
                          <div data-ro="1"><dt>SSID</dt><dd>${t.ssid || "— not reported"}</dd></div>` : null}
                        ${t.presence ? html`
                          <div><dt>Presence</dt><dd>${t.presence}</dd></div>` : null}
                        ${t.link ? html`
                          <div><dt>Link</dt><dd>${t.link}</dd></div>` : null}
                      </dl>` : null}
                  </div>`)}
                ${targets.length > 12
                  ? html`<div className="cmc-drawer__row"><span>+${targets.length - 12} more</span></div>`
                  : null}
              </div>
            </section>

            ${effectRows.length ? html`
              <section className="cmc-drawer__sec cmc-drawer__effects" aria-label="What happens">
                <header className="cmc-drawer__sec-hd">
                  <span>What happens</span>
                  <b>${effectRows.length}</b>
                </header>
                <ul className="cmc-drawer__effects-list">
                  ${effectRows.map((line, i) => html`<li key=${i}>${line}</li>`)}
                </ul>
              </section>` : null}

            ${needAck ? html`
              <label className="cmc-drawer__ack">
                <input type="checkbox" checked=${acked}
                  onChange=${(e) => setAcked(!!e.target.checked)}/>
                <span>${ackLabel
                  || "I checked the unit, SSID, and delay — arm this restart."}</span>
              </label>` : null}

            <div className="cmc-drawer__acts btn-row">
              <button type="button" className="btn btn--ghost" onClick=${onNo}>Cancel</button>
              <button type="button"
                className=${reboot ? "btn btn--danger" : "btn btn--go"}
                disabled=${needAck && !acked}
                autofocus=${!(needAck)}
                onClick=${() => { if (!needAck || acked) onYes(); }}>
                ${confirmLabel || "Confirm send"}
              </button>
            </div>
          </div>
        <//>
      <//>
    <//>`;
}

/**
 * @param {{
 *   kind: string,
 *   targets: Array<{id?:string,label?:string,ssid?:string,presence?:string,link?:string}|string>,
 *   detail?: string,
 *   confirmLabel?: string,
 *   facts?: Array<{k?:string,label?:string,v?:string,tone?:string,readOnly?:boolean}>,
 *   effects?: string[],
 *   ackLabel?: string,
 * }} opts
 * @returns {Promise<boolean>}
 */
export function confirmDeviceCommand(opts) {
  const kind = String((opts && opts.kind) || "").toLowerCase();
  const targets = Array.isArray(opts && opts.targets) ? opts.targets : [];
  if (!targets.length) return Promise.resolve(false);

  const host = document.createElement("div");
  host.setAttribute("data-cmc-safe-host", "1");
  document.body.appendChild(host);
  const root = createRoot(host);

  return new Promise((resolve) => {
    const finish = (ok) => {
      try { root.unmount(); } catch (_e) { /* ignore */ }
      try { host.remove(); } catch (_e) { /* ignore */ }
      resolve(!!ok);
    };
    root.render(html`
      <${ConfirmDrawer}
        kind=${kind}
        title=${labelOf(kind)}
        targets=${targets}
        detail=${opts && opts.detail}
        confirmLabel=${opts && opts.confirmLabel}
        facts=${(opts && opts.facts) || []}
        effects=${(opts && opts.effects) || []}
        ackLabel=${(opts && opts.ackLabel) || ""}
        onYes=${() => finish(true)}
        onNo=${() => finish(false)}
      />`);
  });
}
